// src/cockpit4/components/DebateRoom.tsx
// ============================================
// 多角色议题辩论室（v6.45 新增，对标李开复 WAIC2026「多智能体决策辩论」）
//
// 定位：把「老板一个人拍脑袋」变成「战略/风控/执行多角色辩论 + 主席裁决」，
//       让重要决策先被 AI 多方论证一遍，再落地。
//
// 思路（李开复：AI 进决策核心环节，而不是边缘）：
//   老板会忘记，老板 AI 不会忘记；公司存活靠决策/判断/战略，AI 应进这些核心环节。
//   本组件用三个角色（战略侧 / 风控侧 / 执行侧）+ 一名主席，围绕一个议题做论证与裁决，
//   产出「结论 + 行动项 + 风险红线」，可直接作为会议输入或写入 Vault。
//
// 省 token 设计：战略侧先独立出主稿（1 次真实调用），再由主席合并生成
//               「风控 + 执行 + 裁决」三合一（第 2 次调用），共 2 次调用完成完整辩论。
// ============================================

import { useState } from "react";
import {
  MessageSquare, Play, Loader2, RefreshCw, RotateCcw,
} from "lucide-react";

// ============================================
// 类型
// ============================================

type Role = "strategy" | "risk" | "exec";

interface Turn {
  role: Role;
  label: string;
  icon: string;
  content: string;
  color: string;
}

// ============================================
// 工具
// ============================================

/** 从懒人三段式文本中提取某个段落（含界定词即命中，模糊兜底返回剩文） */
function extractSection(text: string, tag: string): string {
  const idx = text.indexOf(`【${tag}】`);
  if (idx === -1) return "";
  const rest = text.slice(idx + `【${tag}】`.length).trim();
  // 截到下一个【 之前
  const next = rest.indexOf("【");
  return (next === -1 ? rest : rest.slice(0, next)).trim();
}

const ROLE_META: Record<Role, { label: string; icon: string; color: string }> = {
  strategy: { label: "战略侧", icon: "🎯", color: "#3B82F6" },
  risk: { label: "风控侧", icon: "🛡", color: "#F59E0B" },
  exec: { label: "执行侧", icon: "⚡", color: "#10B981" },
};

const CARD = "rounded-xl border border-[#1e2a3f]/60 bg-[#0a0e1a]/60 backdrop-blur-md p-3";

// ============================================
// 主组件
// ============================================

export default function DebateRoom() {
  const [topic, setTopic] = useState("");
  const [turns, setTurns] = useState<Turn[]>([]);
  const [phase, setPhase] = useState<"idle" | "debating" | "done">("idle");
  const [chair, setChair] = useState("");

  // 申请辩论（两阶段调用 DeepSeek）
  const startDebate = async () => {
    const t = topic.trim();
    if (!t) return;
    setPhase("debating");
    setChair("");
    const newTurns: Turn[] = [];

    // ===== 第一步：战略侧独立主稿（1 次真实调用） =====
    newTurns.push({ role: "strategy", ...ROLE_META.strategy, content: "…" });
    setTurns([...newTurns]);
    let strategyText = "";
    try {
      const resp = await fetch("/deepseek-api", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "deepseek-v4-flash",
          temperature: 0.4,
          max_tokens: 900,
          messages: [
            {
              role: "system",
              content:
                "你是公司的「战略侧」评审，用第一性原理思考。针对议题给出结构化判断：" +
                "利害分析、与公司方向契合度、长期价值、你的支持/反对及理由、可执行建议。语气专业、克制。中文输出。",
            },
            { role: "user", content: `议题：${t}` },
          ],
        }),
      });
      const data = await resp.json();
      strategyText = String(data?.content || "").trim();
      newTurns[0] = { ...newTurns[0], content: strategyText || "（战略侧未返回内容）" };
      setTurns([...newTurns]);
    } catch {
      newTurns[0] = { ...newTurns[0], content: "（战略侧调用失败，请检查 DEEPSEEK_API_KEY 或网络后重试）" };
      setTurns([...newTurns]);
      setPhase("done");
      return;
    }

    // ===== 第二步：主席合并生成「风控 + 执行 + 裁决」（1 次调用，省 token） =====
    const risk: Turn = { role: "risk", ...ROLE_META.risk, content: "…" };
    const exec: Turn = { role: "exec", ...ROLE_META.exec, content: "…" };
    newTurns.push(risk, exec);
    setTurns([...newTurns]);
    try {
      const resp = await fetch("/deepseek-api", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "deepseek-v4-flash",
          temperature: 0.4,
          max_tokens: 1200,
          messages: [
            {
              role: "system",
              content:
                "你既是「风控侧」争论者，也是「执行侧」争论者，最后以「主席」身份裁决。请严格按三段输出（中文），每段格式为【风控侧】【执行侧】【主席裁决】。",
            },
            {
              role: "user",
              content:
                `议题：${t}\n\n【战略侧先行观点】\n${strategyText}\n\n` +
                "请输出：\n【风控侧】该议题的主要风险与反问（100-160字）\n" +
                "【执行侧】该议题落地的可行性与具体步骤（100-160字）\n" +
                "【主席裁决】综合三方给结论（支持/反对/有条件支持），以及 <=3 条行动项 + 1 条风险红线。",
            },
          ],
        }),
      });
      const data = await resp.json();
      const chairText = String(data?.content || "").trim();
      const riskTxt = extractSection(chairText, "风控侧");
      const execTxt = extractSection(chairText, "执行侧");
      newTurns[1] = { ...newTurns[1], content: riskTxt || "（风险侧观点见主席裁决）" };
      newTurns[2] = { ...newTurns[2], content: execTxt || "（执行侧观点见主席裁决）" };
      setTurns([...newTurns]);
      setChair(extractSection(chairText, "主席裁决") || chairText);
    } catch {
      newTurns[1] = { ...newTurns[1], content: "（风控侧调用失败）" };
      newTurns[2] = { ...newTurns[2], content: "（执行侧调用失败）" };
      setTurns([...newTurns]);
      setChair("（裁决调用失败，请检查服务后重试）");
    }
    setPhase("done");
  };

  const reset = () => {
    setTurns([]);
    setChair("");
    setPhase("idle");
  };

  return (
    <div className={`${CARD} space-y-3`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <MessageSquare className="w-3.5 h-3.5 text-sky-400" />
          <span className="text-[11px] font-semibold text-white">多角色议题辩论室</span>
          <span className="text-[9px] font-mono text-gray-500">战略 · 风控 · 执行 + 主席裁决</span>
        </div>
        {phase === "done" && (
          <button onClick={reset} className="flex items-center gap-1 text-[9px] font-mono text-gray-500 hover:text-gray-300">
            <RotateCcw className="w-3 h-3" />新议题
          </button>
        )}
      </div>

      {/* 议题输入 */}
      <div className="flex gap-2">
        <input
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && phase !== "debating" && startDebate()}
          placeholder="输入要论证的议题，例如：是否投入 3 万做 GEO 收录收录冲刺？"
          disabled={phase === "debating"}
          className="flex-1 text-[11px] bg-[#05070d] border border-white/10 rounded-md px-2.5 py-2 text-gray-200 outline-none focus:border-sky-500/50"
        />
        <button
          onClick={startDebate}
          disabled={phase === "debating" || !topic.trim()}
          className="flex items-center gap-1.5 px-3 rounded-md text-[11px] font-mono bg-sky-500/20 border border-sky-500/40 text-sky-200 hover:bg-sky-500/30 disabled:opacity-40"
        >
          {phase === "debating" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
          {phase === "debating" ? "辩论中…" : "开始辩论"}
        </button>
      </div>

      {/* 辩论回合 */}
      <div className="space-y-2">
        {phase === "idle" && (
          <div className="text-[10px] font-mono text-gray-500 text-center py-6 leading-relaxed">
            老板会忘记，老板 AI 不会忘记。
            <br />重要决策先让 AI 多方论证一遍，再落地。
          </div>
        )}
        {turns.map((turn, i) => {
          const mine = turn.content;
          const loading = mine === "…";
          return (
            <div key={i} className="flex items-start gap-2 rounded-lg bg-white/[0.02] border border-white/[0.05] px-2.5 py-2">
              <span className="w-6 h-6 rounded-md flex items-center justify-center text-sm flex-shrink-0"
                style={{ background: `${turn.color}1a`, border: `1px solid ${turn.color}44` }}>
                {turn.icon}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-semibold mb-0.5" style={{ color: turn.color }}>{turn.label}</div>
                {loading ? (
                  <div className="flex items-center gap-1.5 text-gray-500 text-[10px] font-mono">
                    <Loader2 className="w-3 h-3 animate-spin" />思考中…
                  </div>
                ) : (
                  <div className="text-[10px] font-mono text-gray-300 leading-relaxed whitespace-pre-wrap">{mine}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* 主席裁决 */}
      {chair ? (
        <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/[0.06] px-3 py-2.5">
          <div className="flex items-center gap-1.5 mb-1">
            <span className="text-sm">⚖️</span>
            <span className="text-[10px] font-semibold text-emerald-300">主席裁决</span>
          </div>
          <div className="text-[10px] font-mono text-gray-200 leading-relaxed whitespace-pre-wrap">{chair}</div>
        </div>
      ) : (
        phase === "done" && (
          <div className="text-[10px] font-mono text-gray-500">主席裁决生成中…</div>
        )
      )}

      <div className="text-[8px] font-mono text-gray-600">
        多一轮论证少一分拍脑袋风险 · 议题与裁决可作为会议输入，如需留档可抄录到会议纪要
      </div>
    </div>
  );
}